
import React from 'react';
import { RotateCcw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompressionResult } from '@/types/imageFile';
import { downloadFile } from '@/utils/imageCompression';
import { useAnalytics } from '@/hooks/useAnalytics';
import { compressorText, Locale } from '@/i18n/compressor';

interface ActionButtonsProps {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  retryCount: number;
  maxRetryAttempts: number;
  compression?: CompressionResult;
  fileName: string;
  onCompress: () => void;
  onRetry: () => void;
  isPremium?: boolean;
  locale?: Locale;
}

const ActionButtons = ({
  status,
  retryCount,
  maxRetryAttempts,
  compression,
  fileName,
  onRetry,
  isPremium = false,
  locale = 'es'
}: ActionButtonsProps) => {
  const { trackEvent } = useAnalytics();
  const t = compressorText[locale].actionButtons;

  const handleDownloadCompressed = () => {
    if (compression) {
      const compressedKb = Math.round(compression.compressedSize / 1024);
      const reductionPercent = Math.round(compression.compressionRatio);
      const fileExt = compression.compressedFile.type || fileName.split('.').pop() || 'unknown';
      trackEvent('download_click', {
        file_type: fileExt,
        compressed_size_kb: compressedKb,
        reduction_percent: reductionPercent,
      });
      downloadFile(compression.compressedFile, `compressed_${fileName}`);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {status === 'completed' && compression && (
        <div>
          <Button
            onClick={handleDownloadCompressed}
            size="lg"
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3"
          >
            <Download className="w-5 h-5 mr-2" />
            {t.downloadOptimized}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-1">{t.readyToUse}</p>
        </div>
      )}

      {status === 'failed' && retryCount < maxRetryAttempts && (
        <Button
          onClick={onRetry}
          size="sm"
          variant="outline"
          className="w-full"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          {t.retry}
        </Button>
      )}

      {status === 'failed' && retryCount >= maxRetryAttempts && (
        <div className="text-center text-xs text-destructive py-2">
          {t.maxRetriesReached}
        </div>
      )}
    </div>
  );
};

export default ActionButtons;
